import { Injectable, NgZone, inject } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ComponentType } from '@angular/cdk/portal';
import { TranslateService } from '@ngx-translate/core';
import { AlertComponent } from '../components/common/alert/alert.component';
import { AlertData } from '../common/models/common/alert/alert-data';
import { AlertButton, AlertController } from '@ionic/angular';
import { MepActionButton } from '../common';
import { PlatformService } from './platform.service';

@Injectable({
  providedIn: 'root',
})
export class AlertService {
  private _alertController: any;

  constructor(
    private _platformService: PlatformService,
    private _zone: NgZone,
    public translateService: TranslateService
  ) {
    if (this._platformService.platform === 'mobile') {
      this._alertController = inject(AlertController);
    } else {
      this._alertController = inject(MatDialog);
    }
  }

  public async showAlert(title: string, message: string): Promise<any> {
    return this._zone.run(() => {
      if (this._platformService.platform === 'mobile') {
        return this.showConfirmAlert(message);
      }

      const openedDialog = this._alertController.open(AlertComponent, {
        data: {
          title,
          message,
          type: 'alert',
          neutralAction: 'OK',
        } as AlertData,
      });

      return new Promise((resolve, reject) => {
        openedDialog.afterClosed().subscribe((response: any) => {
          resolve(true);
          reject();
        });
      });
    });
  }

  public async showError(title: string, message: string): Promise<any> {
    if (this._platformService.platform === 'mobile') {
      return this.showConfirmAlert(message);
    }

    const openedDialog = this._alertController.open(AlertComponent, {
      data: {
        title,
        message,
        type: 'error',
        neutralAction: 'OK',
      } as AlertData,
    });

    return new Promise((resolve, reject) => {
      openedDialog.afterClosed().subscribe((response: any) => {
        resolve(response);
        reject();
      });
    });
  }

  public async showConfirmation(
    title: string,
    message: string,
    positiveLabel: string,
    negativeLabel: string,
    actionHandler?: (payload: string) => void,
    translateMessage: boolean = true
  ): Promise<any> {
    positiveLabel = this.translateService.instant(positiveLabel);
    negativeLabel = this.translateService.instant(negativeLabel);

    if (this._platformService.platform === 'mobile') {
      return this.showConfirmationAlert(message, positiveLabel, negativeLabel);
    }

    const openedDialog = this._alertController.open(AlertComponent, {
      data: {
        title:
          title && title !== '' ? this.translateService.instant(title) : '',
        message:
          message && message !== ''
            ? translateMessage
              ? this.translateService.instant(message)
              : message
            : '',
        type: 'confirmation',
        negativeAction: negativeLabel,
        positiveAction: positiveLabel,
        onAction: actionHandler,
      } as AlertData,
    });

    return new Promise((resolve, reject) => {
      openedDialog.afterClosed().subscribe((response: any) => {
        resolve(response);
        reject();
      });
    });
  }

  public async showComponent<T, R = any>(
    component: ComponentType<T>,
    data?: any,
    config?: MatDialogConfig
  ): Promise<R> {
    if (this._platformService.platform === 'mobile') {
      await this.showConfirmAlert(
        this.translateService.instant('COMMON.FEATURE_NOT_SUPPORTED_ON_MOBILE')
      );
      return null as unknown as R;
    }

    const openedDialog = this._alertController.open(component, {
      data,
      ...config,
    });

    return new Promise((resolve) => {
      openedDialog.afterClosed().subscribe((response: R) => {
        resolve(response);
      });
    });
  }

  //mobile methods
  public async showConfirmationAlert(
    message: string,
    positiveLabel: string,
    negativeLabel: string
  ): Promise<boolean> {
    const buttons: MepActionButton[] = [
      {
        text: positiveLabel,
        role: 'save',
      },
      {
        text: negativeLabel,
        role: 'cancel',
      },
    ];

    const result = await this.showActionsAlert(message, buttons);

    return result.role === 'save';
  }

  public async showActionsAlert(
    message: string,
    buttons: MepActionButton[]
  ): Promise<any> {
    const buttonsAlert: AlertButton[] = [];

    buttons.forEach((button) => {
      buttonsAlert.push({
        text: button.text,
        role: button.role,
      });
    });

    const alert = await this._alertController.create({
      message: this.translateService.instant(message),
      buttons: buttons,
    });

    await alert.present();

    const result = await alert.onDidDismiss();
    return result;
  }

  public async showConfirmAlert(message: string): Promise<any> {
    const alert = await this._alertController.create({
      message: this.translateService.instant(message),
      buttons: ['Ok'],
    });

    await alert.present();

    const result = await alert.onDidDismiss();
    return result;
  }
}
